import { Select, SelectProps } from 'antd'
import { useEffect, useState } from 'react'
import { apiGetListCompanies } from '../../api'
import defaultParams from '../../assets/constants/default_params'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { ICompany } from '../../models/company.modal'

// Module-level cache — company list is small and stable.
let cachedCompanies: ICompany[] | null = null
let inFlight: Promise<ICompany[]> | null = null

const loadCompanies = async (): Promise<ICompany[]> => {
    if (cachedCompanies) return cachedCompanies
    if (inFlight) return inFlight
    inFlight = apiGetListCompanies({ limit: defaultParams.MAX_LIMIT }).then((resp: any) => {
        if (resp?.data?.companies == null) {
            cachedCompanies = []
            return cachedCompanies
        }
        cachedCompanies = resp.data.companies as ICompany[]
        return cachedCompanies ?? []
    }).finally(() => {
        inFlight = null
    })
    return inFlight
}

type Props = Omit<SelectProps, 'options' | 'filterOption'> & {
    multiple?: boolean
    onlyWithOutlets?: boolean
}

export default function CompanySelect({ multiple, onlyWithOutlets, ...rest }: Props) {
    const [companies, setCompanies] = useState<ICompany[]>(cachedCompanies ?? [])

    useEffect(() => {
        let mounted = true
        loadCompanies().then(list => {
            if (mounted) setCompanies(list)
        })
        return () => { mounted = false }
    }, [])

    const options = companies
        .filter(c => !onlyWithOutlets || c.hasOutlets)
        .map(c => ({
            label: `${c.name} (${c.code})`,
            value: String(c.code ?? ''),
        }))

    return (
        <Select
            {...rest}
            mode={multiple ? 'multiple' : undefined}
            showSearch
            allowClear
            options={options}
            filterOption={filterOptionByLabel}
            style={{ minWidth: 200, ...(rest.style ?? {}) }}
        />
    )
}
